import {SGUAI} from "../SGUAI";
import {
  findDisplayProfile,
  findDisplayProfileForDevice,
  publicDisplayProfile,
  SGUAI_DISPLAY_PROFILES,
} from "./displayProfiles";
import {
  decodeSguaiResponse,
  getCommandSupport,
  getSguaiCommand,
  SGUAI_COMMANDS,
  uint16Payload,
} from "./sguaiCommands";

const readResponseByte = (responses, command, offset = 5) => (
  responses?.[command]?.byteLength > offset ? responses[command].getUint8(offset) : null
);

const wait = (milliseconds) => new Promise((resolve) => setTimeout(resolve, milliseconds));
const encodeUtf8 = (value) => {
  const encoded = encodeURIComponent(value);
  const bytes = [];
  for (let index = 0; index < encoded.length; index++) {
    if (encoded[index] === "%") {
      bytes.push(parseInt(encoded.slice(index + 1, index + 3), 16));
      index += 2;
    } else {
      bytes.push(encoded.charCodeAt(index));
    }
  }
  return bytes;
};

export class SguaiAdapter {
  id = "sguai";
  brandName = "SGUAI";
  displayName = "SGUAI pixel display";

  constructor(protocol = new SGUAI()) {
    this.protocol = protocol;
    this.selectedProfileId = SGUAI_DISPLAY_PROFILES[0].id;
    this.lastTimeSync = null;
    this.timeSyncError = null;
    this.connectionListeners = new Set();
    this.commandListeners = new Set();

    this.protocol.disconnectionCallback = () => {
      this.emitConnection(false);
    };
    this.protocol.setConnected = () => {
      this.emitConnection(Date.now());
    };
    this.protocol.setCommand = (event) => {
      this.emitCommand(event);
    };
  }

  get connected() {
    return Boolean(this.protocol.connected);
  }

  get capabilities() {
    return {
      bitmap: {
        ...this.getDisplayProfile(),
        profiles: this.listDisplayProfiles(),
      },
      animation: true,
      effects: true,
      text: {maxLength: 50},
      displayTrigger: true,
      temperatureUnitToggle: true,
      timeSync: true,
      commands: {
        count: SGUAI_COMMANDS.length,
        expertMode: true,
      },
    };
  }

  get deviceName() {
    return this.protocol.device?.name ?? this.displayName;
  }

  onConnectionChange(listener) {
    this.connectionListeners.add(listener);
    return () => this.connectionListeners.delete(listener);
  }

  onCommand(listener) {
    this.commandListeners.add(listener);
    return () => this.commandListeners.delete(listener);
  }

  emitConnection(value) {
    this.connectionListeners.forEach((listener) => listener(value));
  }

  emitCommand(event) {
    this.commandListeners.forEach((listener) => listener(event));
  }

  listCommands(deviceName = this.protocol.device?.name ?? "") {
    return SGUAI_COMMANDS.map((definition) => ({
      ...definition,
      support: getCommandSupport(definition.command, deviceName),
    }));
  }

  getCommandDefinition(commandOrId) {
    return getSguaiCommand(commandOrId);
  }

  getCommandResponse(commandOrId) {
    const definition = this.requireCommand(commandOrId);
    const response = this.protocol.responses?.[definition.command];
    return response ? decodeSguaiResponse(definition.command, response) : null;
  }

  async readCommand(commandOrId, payload, options = {}) {
    const definition = this.requireCommand(commandOrId);
    if (definition.read === undefined && !options.force) {
      throw Error(`${definition.name} is not a read command`);
    }
    const args = payload ?? definition.read ?? [];
    const response = await this.protocol.read(definition.command, this.validatePayload(args));
    return decodeSguaiResponse(definition.command, response);
  }

  async writeCommand(commandOrId, payload = [], options = {}) {
    const definition = this.requireCommand(commandOrId);
    if (!definition.write && !options.force) {
      throw Error(`${definition.name} is not a write command`);
    }
    this.requireSafetyConfirmation(definition, options);
    await this.protocol.send(definition.command, this.validatePayload(payload));
    return this.getCommandResponse(definition.command);
  }

  async runCommand(commandOrId, payload, options = {}) {
    const definition = this.requireCommand(commandOrId);
    if (!definition.action) throw Error(`${definition.name} is not an action command`);
    this.requireSafetyConfirmation(definition, options);
    const args = payload ?? definition.action.payload ?? [];
    await this.protocol.sendCommand(
      definition.command,
      definition.action.direction,
      this.validatePayload(args)
    );
  }

  async connect() {
    this.lastTimeSync = null;
    this.timeSyncError = null;
    await this.protocol.connect();
    const detectedProfile = findDisplayProfileForDevice(
      SGUAI_DISPLAY_PROFILES,
      this.protocol.device?.name
    );
    if (detectedProfile) this.selectedProfileId = detectedProfile.id;
    try {
      await this.syncTime({emit: false});
    } catch (error) {
      // Clock support varies by model; a rejected sync must not drop the BLE session.
    }
    this.emitConnection(true);
    return this;
  }

  async disconnect() {
    await this.protocol.disconnect?.();
    this.emitConnection(false);
  }

  async refreshStatus() {
    await this.protocol.refreshAll();
    this.emitConnection(this.connected);
    return this.getStatus();
  }

  getStatus() {
    const responses = this.protocol.responses;
    return {
      battery: readResponseByte(responses, 0x2),
      temperature: readResponseByte(responses, 0x1),
      temperatureMode: readResponseByte(responses, 0xb) ?? 0,
      firmwareVersion: this.getFirmwareVersion(),
    };
  }

  getClockStatus() {
    let timezone = "Local time";
    try {
      timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || timezone;
    } catch (error) {
      // Some embedded browsers do not expose an IANA timezone name.
    }
    return {
      syncedAt: this.lastTimeSync?.toISOString() ?? null,
      error: this.timeSyncError,
      timezone,
    };
  }

  getFirmwareVersion() {
    const response = this.protocol.responses?.[0x9];
    if (!response || response.byteLength < 7) return null;
    const payloadEnd = response.byteLength - 2;
    return `${response.getUint8(payloadEnd - 2)}.${response.getUint8(payloadEnd - 1)}`;
  }

  getFunctionSwitch(feature) {
    const response = this.protocol.responses?.[0x21];
    if (!response || response.byteLength < 9) return null;
    for (let offset = 5; offset < response.byteLength - 3; offset += 2) {
      if (response.getUint8(offset) === feature) {
        return response.getUint8(offset + 1);
      }
    }
    return null;
  }

  getEffects() {
    const responses = this.protocol.responses;
    return {
      animationMode: readResponseByte(responses, 0x23) ?? 0,
      speed: readResponseByte(responses, 0x24) ?? 20,
      screenTimeout: readResponseByte(responses, 0x27) ?? 0,
      displayTrigger: this.getFunctionSwitch(0x25),
    };
  }

  listDisplayProfiles() {
    return SGUAI_DISPLAY_PROFILES.map(publicDisplayProfile);
  }

  getDisplayProfile() {
    const detected = findDisplayProfileForDevice(
      SGUAI_DISPLAY_PROFILES,
      this.protocol.device?.name
    );
    return publicDisplayProfile(
      detected ?? findDisplayProfile(SGUAI_DISPLAY_PROFILES, this.selectedProfileId)
    );
  }

  setDisplayProfile(profileId) {
    const profile = findDisplayProfile(SGUAI_DISPLAY_PROFILES, profileId);
    this.selectedProfileId = profile.id;
    this.emitConnection(this.connected);
    return publicDisplayProfile(profile);
  }

  async setTemperatureUnit(mode) {
    await this.protocol.send(0xb, [mode]);
    return this.refreshStatus();
  }

  async setScreenTimeout(timeout) {
    await this.protocol.send(0x27, [timeout]);
    await this.protocol.read(0x27);
    this.emitConnection(this.connected);
  }

  async setAnimationMode(mode) {
    await this.protocol.send(0x23, [mode]);
    await this.protocol.read(0x23);
    this.emitConnection(this.connected);
  }

  async setAnimationSpeed(speed) {
    await this.protocol.send(0x24, [speed]);
    await this.protocol.read(0x24);
    this.emitConnection(this.connected);
  }

  async setDisplayTrigger(mode) {
    await this.setFunctionSwitch(0x25, mode);
    await this.protocol.read(0x21);
    this.emitConnection(this.connected);
  }

  async setFunctionSwitch(feature, enabled) {
    await this.protocol.send(0x21, [this.validateByte(feature), this.validateByte(enabled)]);
  }

  async setBrightness(level) {
    await this.protocol.send(0x54, [this.validateRange(level, 0, 3, "Brightness")]);
    await this.protocol.read(0x54);
  }

  async setVolume(level) {
    await this.protocol.send(0x06, [this.validateRange(level, 0, 100, "Volume")]);
    await this.protocol.read(0x06, [0]);
  }

  async setReminderMode(mode) {
    await this.protocol.send(0x0f, [this.validateRange(mode, 0, 1, "Reminder mode")]);
    await this.protocol.read(0x0f, [0]);
  }

  async setPomodoroRepeats(repeats) {
    await this.protocol.send(0x16, [this.validateRange(repeats, 0, 3, "Pomodoro repeats")]);
    await this.protocol.read(0x16);
  }

  async setTargetTemperature(temperature) {
    await this.protocol.send(0x18, [this.validateByte(temperature, "Target temperature")]);
    await this.protocol.read(0x18);
  }

  async setHeater(enabled) {
    await this.protocol.send(0x1a, [enabled ? 1 : 0]);
    await this.protocol.read(0x1a);
  }

  async setClockMode(mode) {
    await this.protocol.send(0x1e, [this.validateRange(mode, 0, 1, "Clock mode")]);
    await this.protocol.read(0x1e);
  }

  async setSterilization(type, enabled) {
    const command = type === "automatic" ? 0x1d : type === "manual" ? 0x1c : null;
    if (command === null) throw Error("Sterilization type must be manual or automatic");
    await this.protocol.send(command, [enabled ? 1 : 0]);
    await this.protocol.read(command);
  }

  async setGradualLight(enabled) {
    await this.protocol.send(0x2a, [enabled ? 1 : 0]);
    await this.protocol.read(0x2a);
  }

  async setReminderColor(color) {
    await this.protocol.send(0x2b, uint16Payload(color));
    await this.protocol.read(0x2b);
  }

  async setDoNotDisturb({enabled, startHour, startMinute, endHour, endMinute}) {
    const payload = [
      enabled ? 1 : 0,
      this.validateRange(startHour, 0, 23, "Start hour"),
      this.validateRange(startMinute, 0, 59, "Start minute"),
      this.validateRange(endHour, 0, 23, "End hour"),
      this.validateRange(endMinute, 0, 59, "End minute"),
    ];
    await this.protocol.send(0x29, payload);
    await this.protocol.read(0x29);
  }

  async setHydrationGoal(milliliters) {
    await this.protocol.send(0x0e, uint16Payload(milliliters));
  }

  async setDailyWater(milliliters) {
    await this.protocol.send(0x12, uint16Payload(milliliters));
  }

  async setReminder({id, enabled, hour, minute, repeat = 0, water = 0}) {
    await this.protocol.send(0x03, [
      0x02,
      this.validateByte(id, "Reminder ID"),
      enabled ? 1 : 0,
      this.validateRange(hour, 0, 23, "Reminder hour"),
      this.validateRange(minute, 0, 59, "Reminder minute"),
      this.validateByte(repeat, "Repeat mask"),
      this.validateByte(water, "Water amount"),
    ]);
  }

  async setReminderLabel(id, text) {
    const singleByteModels = ["SGUAI-T5", "SGUAI-T5S", "SGUAI-C6S"];
    const singleByte = singleByteModels.includes(this.protocol.device?.name);
    const encoded = [];
    for (const character of text) {
      const code = character.charCodeAt(0);
      if (singleByte) encoded.push(code & 0xff);
      else encoded.push((code >>> 8) & 0xff, code & 0xff);
    }
    await this.protocol.send(0x14, [this.validateByte(id, "Reminder ID"), ...encoded]);
  }

  async setStandbyText(text) {
    if (text.length > 50) throw Error("Standby text is limited to 50 characters");
    const encoded = Array.from(text, (character) => character === "…" ? 0x85 : character.charCodeAt(0) & 0xff);
    await this.protocol.send(0x52, [encoded.length ? 1 : 0, ...encoded]);
  }

  async configureWifi(ssid, password) {
    const supported = ["SGUAI-C3R", "SGUAI-C5R"];
    if (!supported.includes(this.protocol.device?.name)) {
      throw Error("Structured Wi-Fi provisioning is verified only for SGUAI-C3R and SGUAI-C5R");
    }
    const ssidBytes = encodeUtf8(ssid);
    const passwordBytes = encodeUtf8(password);
    if (!ssidBytes.length || ssidBytes.length > 255 || passwordBytes.length > 255) {
      throw Error("SSID and password must fit in one-byte UTF-8 length fields");
    }
    await this.protocol.send(0x07, [
      ssidBytes.length,
      ...ssidBytes,
      passwordBytes.length,
      ...passwordBytes,
    ]);
  }

  async calibrateAccelerometer() {
    await this.protocol.send(0x0a, [0]);
  }

  async identify() {
    await this.protocol.send(0x08, [0]);
  }

  async drawBitmap(bitmap) {
    this.validateBitmap(bitmap);
    await this.protocol.send(0x25, bitmap);
  }

  async playAnimationFrames(frames, options = {}) {
    if (!frames.length || frames.length > 255) {
      throw Error("Animations require between 1 and 255 frames");
    }
    frames.forEach((frame) => this.validateBitmap(frame));
    const speed = options.speed ?? options.duration ?? 130;
    const frameDelay = options.frameDelay ?? 150;
    const retries = options.retries ?? 3;
    await this.sendWithRetry(0x26, [frames.length, speed], retries);
    if (frameDelay) await wait(frameDelay);
    for (let i = 0; i < frames.length; i++) {
      await this.sendWithRetry(0x26, [i, speed, ...frames[i]], retries);
      if (frameDelay && i < frames.length - 1) await wait(frameDelay);
    }
  }

  async showText(encodedText) {
    if (encodedText.length > 100) throw Error("SGUAI text is limited to 50 characters");
    await this.protocol.send(0x17, [encodedText.length ? 1 : 0, ...encodedText]);
  }

  async syncTime(options = {}) {
    try {
      await this.protocol.syncTime();
      this.lastTimeSync = new Date();
      this.timeSyncError = null;
    } catch (error) {
      this.timeSyncError = error instanceof Error ? error.message : String(error);
      if (options.emit !== false) this.emitConnection(this.connected);
      throw error;
    }
    if (options.emit !== false) this.emitConnection(this.connected);
    return this.getClockStatus();
  }

  validateBitmap(bitmap) {
    const profile = this.getDisplayProfile();
    const expectedBytes = Math.ceil(profile.width / 8) * profile.height;
    if (bitmap.length !== expectedBytes) {
      throw Error(`${profile.name} requires a ${expectedBytes}-byte bitmap`);
    }
  }

  requireCommand(commandOrId) {
    const definition = getSguaiCommand(commandOrId);
    if (!definition) throw Error(`Unknown SGUAI command: ${commandOrId}`);
    return definition;
  }

  requireSafetyConfirmation(definition, options) {
    if (definition.safety !== "safe" && !options.confirmed) {
      throw Error(`${definition.name} requires explicit confirmation`);
    }
  }

  validatePayload(payload) {
    return Array.from(payload, (value) => this.validateByte(value));
  }

  validateByte(value, label = "Payload byte") {
    return this.validateRange(value, 0, 255, label);
  }

  validateRange(value, minimum, maximum, label) {
    const number = Number(value);
    if (!Number.isInteger(number) || number < minimum || number > maximum) {
      throw Error(`${label} must be an integer from ${minimum} to ${maximum}`);
    }
    return number;
  }

  async sendWithRetry(command, args, retries) {
    let lastError;
    for (let attempt = 0; attempt <= retries; attempt++) {
      try {
        await this.protocol.send(command, args);
        return;
      } catch (error) {
        lastError = error;
        if (attempt < retries) await wait(100);
      }
    }
    throw lastError;
  }
}
